import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// True only for a genuine auth rejection (expired/invalid session).
const isAuthRejection = (e) => {
  const s = e?.response?.status;
  return s === 401 || s === 403;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("kys_token");
    if (!token) {
      setUser(false);
      setChecked(true);
      return;
    }

    // Verify the session. On a transient backend outage (waking container,
    // network/timeout), keep the token and retry so the admin is NOT kicked
    // out to a login/customer page just because the server was slow.
    (async () => {
      for (let attempt = 0; attempt < 6; attempt++) {
        try {
          const r = await api.get("/auth/me", { timeout: 12000 });
          if (cancelled) return;
          setUser(r.data);
          setChecked(true);
          return;
        } catch (e) {
          if (cancelled) return;
          if (isAuthRejection(e)) {
            localStorage.removeItem("kys_token");
            setUser(false);
            setChecked(true);
            return;
          }
          // transient error → back off and retry (2s, 3s, 4s, ...)
          await sleep(2000 + attempt * 1000);
        }
      }
      // Backend never responded. Keep the token (do NOT log out) and let the
      // guard keep showing "Loading…"; a manual refresh will re-verify.
      if (!cancelled) setChecked(true);
    })();

    return () => { cancelled = true; };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("kys_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    localStorage.removeItem("kys_token");
    setUser(false);
  };

  return <AuthCtx.Provider value={{ user, checked, login, logout }}>{children}</AuthCtx.Provider>;
}
